import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Token storage utility that works for both web and mobile
 * - Web: Uses localStorage
 * - Mobile: Uses Capacitor Preferences
 */
const TOKEN_KEY = 'token';
const DEVICE_ID_KEY = 'device_id';

const generateDeviceId = () => {
  const randomPart = Math.random().toString(36).slice(2, 12);
  return `dev_${Date.now()}_${randomPart}`;
};

export const tokenStorage = {
  /**
   * Get token from storage
   * @returns {Promise<string|null>} Token or null if not found
   */
  async getToken() {
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor Preferences
        const result = await Preferences.get({ key: TOKEN_KEY });
        return result.value || null;
      } else {
        // Web: Use localStorage
        return localStorage.getItem(TOKEN_KEY) || null;
      }
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },

  /**
   * Set token in storage
   * @param {string} token - JWT token to store
   */
  async setToken(token) {
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor Preferences
        await Preferences.set({ key: TOKEN_KEY, value: token });
      } else {
        // Web: Use localStorage
        localStorage.setItem(TOKEN_KEY, token);
      }
    } catch (error) {
      console.error('Error setting token:', error);
    }
  },

  /**
   * Remove token from storage
   */
  async removeToken() {
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor Preferences
        await Preferences.remove({ key: TOKEN_KEY });
      } else {
        // Web: Use localStorage
        localStorage.removeItem(TOKEN_KEY);
      }
      // Also remove from sessionStorage for web (cleanup)
      if (!Capacitor.isNativePlatform()) {
        sessionStorage.removeItem(TOKEN_KEY);
      }
    } catch (error) {
      console.error('Error removing token:', error);
    }
  },

  /**
   * Synchronous get token (for initial load - web only)
   * For mobile, use getToken() which is async
   * @returns {string|null} Token or null
   */
  getTokenSync() {
    if (Capacitor.isNativePlatform()) {
      // For mobile, return null and use async getToken() instead
      return null;
    }
    return localStorage.getItem(TOKEN_KEY) || null;
  },

  async getOrCreateDeviceId() {
    try {
      if (Capacitor.isNativePlatform()) {
        const existing = await Preferences.get({ key: DEVICE_ID_KEY });
        if (existing.value) {
          return existing.value;
        }
        const created = generateDeviceId();
        await Preferences.set({ key: DEVICE_ID_KEY, value: created });
        return created;
      }

      const existing = localStorage.getItem(DEVICE_ID_KEY);
      if (existing) {
        return existing;
      }
      const created = generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, created);
      return created;
    } catch (error) {
      console.error('Error getting/creating device id:', error);
      return 'unknown-device';
    }
  }
};

